import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarHeart, MessageCircle, NotebookPen, ReceiptText } from 'lucide-react';
import { ENDPOINTS, getAuthHeaders, removeAuthToken } from './config';

// The notes section of /api/context is capped server-side; anything at the cap
// is shown as "50+" rather than pretending it is an exact count.
const NOTES_LIMIT = 50;

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// "March 2026" -> "Mar 2026" so the status line stays on one line on mobile.
const shortMonth = (label) => {
  if (!label) return '';
  const [month, ...rest] = String(label).split(' ');
  return [month.slice(0, 3), ...rest].join(' ');
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Turn one /api/context snapshot into a short status string per card.
 * Every section is optional: if the backend returned null for a section (or a
 * shape we do not recognise) that card simply gets no status line, rather than
 * a misleading zero.
 */
export const buildCardStatuses = (context) => {
  const statuses = { bingo: null, wedding: null, expenses: null, notes: null };
  if (!context || typeof context !== 'object') return statuses;

  const { bingo, wedding, expenses, notes } = context;

  if (bingo && bingo.kate && bingo.nav) {
    statuses.bingo = `Kate ${bingo.kate.completed}/${bingo.kate.total} · Nav ${bingo.nav.completed}/${bingo.nav.total}`;
  }

  if (wedding) {
    const nextOpen = Array.isArray(wedding.open_months) ? wedding.open_months[0] : null;
    const openTasks = wedding.tasks ? wedding.tasks.open_tasks : undefined;
    if (nextOpen && nextOpen.month) {
      const overdue = nextOpen.month < currentMonthKey();
      statuses.wedding = `${overdue ? 'Overdue' : 'Next'} ${shortMonth(nextOpen.label)} · ${plural(nextOpen.open, 'task')}`;
    } else if (typeof openTasks === 'number') {
      statuses.wedding = openTasks === 0 ? 'All tasks done' : `${plural(openTasks, 'task')} open`;
    }
  }

  if (expenses && Array.isArray(expenses.balances)) {
    const open = expenses.balances.filter((b) => b && b.direction && b.direction !== 'settled');
    if (open.length === 0) {
      statuses.expenses = expenses.balances.length ? 'All settled' : 'No expenses yet';
    } else {
      const currencies = open.map((b) => b.currency).filter(Boolean);
      const shown = currencies.slice(0, 3).join(', ');
      statuses.expenses = `Open in ${shown}${currencies.length > 3 ? ` +${currencies.length - 3}` : ''}`;
    }
  }

  if (notes && Array.isArray(notes.open)) {
    const count = notes.open.length;
    statuses.notes = count === 0
      ? 'Nothing open'
      : `${count}${count >= NOTES_LIMIT ? '+' : ''} open ${count === 1 ? 'item' : 'items'}`;
  }

  return statuses;
};

/**
 * Concise secondary line under a card title. Renders a quiet placeholder while
 * the snapshot loads and nothing at all if the data never arrived.
 */
const StatusLine = ({ loading, text, tone }) => {
  if (loading) {
    return <span className="mt-1.5 block h-2.5 w-24 rounded-full bg-stone-200 animate-pulse" aria-hidden="true" />;
  }
  if (!text) return null;
  return <p className={`mt-1.5 text-[11px] font-medium leading-snug ${tone}`}>{text}</p>;
};

const SystemChoice = () => {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState({ bingo: null, wedding: null, expenses: null, notes: null });
  const [loadingStatuses, setLoadingStatuses] = useState(true);

  // One call for the whole page — /api/context returns a snapshot of every app.
  useEffect(() => {
    const controller = new AbortController();

    const loadContext = async () => {
      try {
        const response = await fetch(ENDPOINTS.CONTEXT, {
          headers: getAuthHeaders(),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Context request failed: ${response.status}`);
        const data = await response.json();
        if (!controller.signal.aborted) setStatuses(buildCardStatuses(data));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Home status lines unavailable:', error);
        }
      } finally {
        if (!controller.signal.aborted) setLoadingStatuses(false);
      }
    };

    loadContext();
    return () => controller.abort();
  }, []);

  const handleLogout = () => {
    removeAuthToken();
    navigate('/login');
  };

  const cardClass =
    'flex items-start gap-3 sm:gap-4 w-full rounded-2xl bg-white p-4 sm:p-5 text-left shadow-sm ring-1 ring-stone-200/80 transition hover:shadow-md hover:ring-stone-300 focus:outline-none group';

  return (
    <div className="min-h-dvh bg-stone-50">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-2 bg-stone-50/80 backdrop-blur border-b border-stone-200/60">
        <span className="text-sm font-semibold text-stone-500 tracking-wide uppercase">Nav &amp; Kate</span>
        <button
          onClick={handleLogout}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center text-stone-500 hover:text-red-500 text-xs font-medium px-3 rounded transition-colors"
        >
          Logout
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-5 pt-6 sm:pt-10 pb-10 sm:pb-16">
        <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-1.5 text-center">
          Home
        </h1>
        <p className="text-sm text-stone-500 mb-5 sm:mb-10 text-center max-w-md mx-auto leading-relaxed">
          Everything you share, in one place — finances, plans, goals, and notes.
        </p>

        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
          {/* AI Navjeev */}
          <button
            className={`${cardClass} focus:ring-2 focus:ring-emerald-400`}
            onClick={() => navigate('/chat')}
          >
            <div className="shrink-0 rounded-xl bg-emerald-100 p-2.5 sm:p-3 group-hover:bg-emerald-200 transition-colors">
              <MessageCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-stone-900 mb-0.5">AI Navjeev</h2>
              <p className="text-xs text-stone-500 leading-relaxed">
                Chat with the AI version of Nav — for company, support, and relationship check-ins.
              </p>
            </div>
          </button>

          {/* Couples Bingo */}
          <button
            className={`${cardClass} focus:ring-2 focus:ring-orange-400`}
            onClick={() => navigate('/bingo')}
          >
            <div className="shrink-0 rounded-xl bg-orange-100 p-2.5 sm:p-3 group-hover:bg-orange-200 transition-colors">
              <span className="block w-6 h-6 text-xl leading-6 text-center">🎲</span>
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-stone-900 mb-0.5">Couples Bingo</h2>
              <p className="text-xs text-stone-500 leading-relaxed">
                Track shared goals and milestones on your 5x5 bingo cards.
              </p>
              <StatusLine loading={loadingStatuses} text={statuses.bingo} tone="text-orange-700" />
            </div>
          </button>

          {/* Wedding Planner */}
          <button
            className={`${cardClass} focus:ring-2 focus:ring-rose-400`}
            onClick={() => navigate('/wedding')}
          >
            <div className="shrink-0 rounded-xl bg-rose-100 p-2.5 sm:p-3 group-hover:bg-rose-200 transition-colors">
              <CalendarHeart className="w-6 h-6 text-rose-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-stone-900 mb-0.5">Wedding Planner</h2>
              <p className="text-xs text-stone-500 leading-relaxed">
                Plan your wedding with a month-by-month timeline and budget.
              </p>
              <StatusLine loading={loadingStatuses} text={statuses.wedding} tone="text-rose-700" />
            </div>
          </button>

          {/* Shared Expenses */}
          <button
            className={`${cardClass} focus:ring-2 focus:ring-slate-400`}
            onClick={() => navigate('/expenses')}
          >
            <div className="shrink-0 rounded-xl bg-slate-100 p-2.5 sm:p-3 group-hover:bg-slate-200 transition-colors">
              <ReceiptText className="w-6 h-6 text-slate-700" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-stone-900 mb-0.5">Shared Expenses</h2>
              <p className="text-xs text-stone-500 leading-relaxed">
                Log spending, track who owes what per currency, and settle up.
              </p>
              <StatusLine loading={loadingStatuses} text={statuses.expenses} tone="text-slate-700" />
            </div>
          </button>

          {/* Inbox */}
          <button
            className={`${cardClass} sm:col-span-2 focus:ring-2 focus:ring-violet-400`}
            onClick={() => navigate('/notes')}
          >
            <div className="shrink-0 rounded-xl bg-violet-100 p-2.5 sm:p-3 group-hover:bg-violet-200 transition-colors">
              <NotebookPen className="w-6 h-6 text-violet-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-stone-900 mb-0.5">Inbox</h2>
              <p className="text-xs text-stone-500 leading-relaxed">
                Shared notes, thoughts, and a live feed of everything happening across your apps.
              </p>
              <StatusLine loading={loadingStatuses} text={statuses.notes} tone="text-violet-700" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemChoice;
